<template>
  <div class="featured-cars-slider" :class="{ 'featured-cars-slider--inverted bg-primary text-white': invertedColor }">
    <div class="featured-cars-slider__swiper-container container container--page-content pr-0">
      <ClientOnly>
        <template #fallback>
          <!-- Server-side fallback with static width and same height as swiper -->
          <div class="featured-cars-slider__swiper mx-0 relative overflow-y-hidden" style="width: 100%; height: 375px">
            <div
              v-for="(car, index) in cars"
              :key="car.documentId"
              class="!w-[305px] h-auto inline-block mr-6 pb-[10px] featured-cars-slider__card--inactive"
            >
              <Card :car="car" :index="index" :tracked="trackedCars.includes(car.documentId)" />
            </div>
          </div>
        </template>

        <swiper-container
          class="featured-cars-slider__swiper mx-0 relative"
          :style="swiperContainerStyle"
          slides-per-view="auto"
          :space-between="width > 768 ? 32 : 24"
          :pagination="{
            el: swiperPagination,
            clickable: false,
          }"
          ref="swiperContainer"
          :slidesOffsetAfter="width > 768 ? 50 : 10"
          slidesOffsetBefore="0"
        >
          <swiper-slide
            v-for="(car, index) in cars"
            :key="car.documentId"
            class="w-[305px] h-auto featured-cars-slider__card--inactive"
          >
            <Card
              :car="car"
              :index="index"
              :tracked="trackedCars.includes(car.documentId)"
              @track-toggle="trackCarToggled(car.documentId)"
              :invertedColor="invertedColor"
            />
          </swiper-slide>
        </swiper-container>
      </ClientOnly>
    </div>
    <div
      class="featured-cars-slider__controls container container--page-content flex justify-between items-center mt-10 md:mt-12"
    >
      <div class="featured-cars-slider__pagination w-full flex gap-2" ref="swiperPagination"></div>
      <div class="featured-cars-slider__navigation flex items-center gap-4">
        <SharedIconButton
          variant="accent"
          @click="swiper?.prev()"
          icon="arrow-left"
          class="w-10 h-10"
          :aria-label="t('swiper.previous')"
          :title="t('swiper.previous')"
          ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12.7069 17.293L8.41394 13H17.9999V11H8.41394L12.7069 6.70697L11.2929 5.29297L4.58594 12L11.2929 18.707L12.7069 17.293Z"
              fill="currentColor"
            />
          </svg>
        </SharedIconButton>
        <SharedIconButton
          variant="accent"
          @click="swiper?.next()"
          icon="arrow-right"
          class="w-10 h-10"
          :aria-label="t('swiper.next')"
          :title="t('swiper.next')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M11.2931 17.293L15.5861 13H6.00006V11H15.5861L11.2931 6.70697L12.7071 5.29297L19.4141 12L12.7071 18.707L11.2931 17.293Z"
              fill="currentColor"
            />
          </svg>
        </SharedIconButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { SwiperContainer } from 'swiper/element';
  import type { SwiperOptions, Swiper } from 'swiper/types';
  import type { AllVehiclesVehicle } from '~/types/api';

  import { useStorage } from '@vueuse/core';

  import Card from './Card.vue';

  defineProps<{
    cars: AllVehiclesVehicle[];
    invertedColor?: boolean;
  }>();

  const { t } = useI18n();

  // Use the globally provided reference container
  const referenceContainerData = inject('referenceContainer') as {
    left: Ref<number>;
    x: Ref<number>;
    width: Ref<number>;
    height: Ref<number>;
  };
  const { width } = useWindowSize();
  const swiperContainer = ref<SwiperContainer | null>(null);
  const { width: swiperContainerWidth } = useElementBounding(swiperContainer);

  const swiperContainerStyle = computed(() => {
    // Calculate the width needed to reach the right edge of the screen
    // We want the swiper to extend from its current position to the right edge
    const windowWidth = width.value;
    const swiperLeftPosition = referenceContainerData.x.value;
    const swiperCurrentWidth = swiperContainerWidth.value;

    // Account for container padding (1.25rem = 20px on mobile, 2rem = 32px on md+)
    // INFO: now we don't need this by default since the wrapper is set to max-width: 100vw;
    // let containerPadding = 0; // 20; // Default padding
    // if (width.value > 768) {
    //   containerPadding = 0;
    // }

    // Calculate how much more width we need to reach the right edge
    // Subtract container padding to avoid overflow
    const additionalWidthNeeded = windowWidth - (swiperLeftPosition + swiperCurrentWidth);

    // Set the width to current width plus the additional space needed
    const computedSwiperWidth = swiperCurrentWidth + additionalWidthNeeded;

    return {
      width: `${Math.min(computedSwiperWidth, 1860)}px`,
    };
  });

  const swiperPagination = ref<HTMLElement | null>(null);
  const swiper = useSwiper(swiperContainer);

  // TODO: this might move to the Cockpit at some point
  const trackedCars = useStorage<string[]>('trackedCars', []);

  const trackCarToggled = (carId: string) => {
    // TODO: this might move to the Cockpit at some point
    if (trackedCars.value.includes(carId)) {
      trackedCars.value = trackedCars.value.filter(id => id !== carId);
    } else {
      trackedCars.value.push(carId);
    }
  };

  const isVisible = useElementVisibility(swiperContainer, {
    rootMargin: '0px 0px -250px 0px',
  });

  const hasAnimated = ref(false);

  onMounted(() => {
    // No need to add classes on mount since they're already in the template
    // Just wait for visibility change to trigger animation
  });

  // Watch for visibility changes and trigger animation
  watch(isVisible, visible => {
    if (visible && !hasAnimated.value) {
      hasAnimated.value = true;
      const container = swiperContainer.value || document.querySelector('.featured-cars-slider__swiper');
      if (container) {
        const slides = container.querySelectorAll('.featured-cars-slider__card--inactive');
        slides.forEach((slide, index) => {
          // Use setTimeout for staggered animation
          setTimeout(() => {
            slide.classList.remove('featured-cars-slider__card--inactive');
            slide.classList.add('featured-cars-slider__card--visible');
          }, index * 100);
        });
      }
    }
  });
</script>

<style lang="scss" scoped>
  .featured-cars-slider__pagination {
    &:deep(.swiper-pagination-bullet) {
      @apply size-2 rounded-full bg-primary;
      @apply mx-0;
      @apply opacity-20;
      @apply pointer-events-none;
    }
    &:deep(.swiper-pagination-bullet-active) {
      @apply bg-primary;
      @apply opacity-100;
    }
  }

  .featured-cars-slider--inverted {
    &:deep(.swiper-pagination-bullet) {
      @apply bg-white;
      @apply pointer-events-none;
    }
    &:deep(.swiper-pagination-bullet-active) {
      @apply bg-white;
    }
  }

  :deep(.featured-cars-slider__card--inactive) {
    opacity: 0;
    transform: translateY(20px);
  }

  :deep(.featured-cars-slider__card--visible) {
    opacity: 1;
    transform: translateY(0);
    animation: fadeInUp 0.6s ease-out forwards;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  :deep(.featured-cars-slider__swiper) {
    display: flex;
    align-items: stretch;
  }

  :deep(swiper-slide) {
    display: flex;
    height: auto;
    padding-bottom: 10px;
  }
</style>
